import { useState, useEffect, useRef, useCallback } from "react"
import { User } from "lucide-react"
import FaxList from "../components/faxList"
import PracticeList from "../components/practiceList"
import FaxView from "../components/faxView"
import { get } from "./../utils/apiClient"
import FormsLogo from "../images/efax.svg"
import "../css/style.css"
import Cookies from "js-cookie"
import { post } from "./../utils/apiClient"

const tabs = [
  {
    key: "inbound",
    label: "Inbound",
  },
  {
    key: "outbound",
    label: "Outbound",
  },
  {
    key: "setting",
    label: "Settings",
  },
]

const Dashboard = ({ authUser }) => {
  const [faxLoading, setFaxLoading] = useState(false)
  const [faxViewWidth, setFaxViewWidth] = useState(30)
  const isResizing = useRef(false)
  const containerRef = useRef(null)

  const onMouseDown = useCallback((e) => {
    e.preventDefault()
    isResizing.current = true
    let hasMoved = false

    const onMouseMove = (e) => {
      if (!isResizing.current || !containerRef.current) return
      hasMoved = true
      const containerRect = containerRef.current.getBoundingClientRect()
      const pct = ((containerRect.right - e.clientX) / containerRect.width) * 100
      setFaxViewWidth(Math.min(50, Math.max(20, pct)))
    }
    const onMouseUp = () => {
      isResizing.current = false
      if (!hasMoved) setFaxViewWidth(30)
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
    }
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)
  }, [])

  const [selectedFax, setSelectedFax] = useState(null)
  const [tab, setTab] = useState("inbound")
  const [selectedPractice, setSelectedPractice] = useState({})
  const [flgReloadFaxes, setFlgReloadFaxes] = useState(false)
  const [practices, setPractices] = useState([])
  const [appLoading, setAppLoading] = useState(true)
  const [flgPracticeCallCompleted, setflgPracticeCallCompleted] =
    useState(false)

  const fetchPractices = async () => {
    try {
      const practicefolders = (await get("/practice/fax-access-details")) || []

      const appConfiguredApps = practicefolders.map((practicefolder) => {
        return {
          practiceId: practicefolder.practiceId,
          practiceName: practicefolder.practiceWithFaxes.practiceName,
          subFolderWithCopiedFaxes: practicefolder.subFolderWithCopiedFaxes,
          appId:
            practicefolder?.practiceWithFaxes?.faxNumberAttributes[0]?.app_id,
          faxNumberAttributes:
            practicefolder.practiceWithFaxes.faxNumberAttributes.map(
              (faxNumberAttribute) => {
                return {
                  userId: faxNumberAttribute.user_id,
                  appId: faxNumberAttribute.app_id,
                  customerKey: faxNumberAttribute.customer_key,
                  faxNumber: faxNumberAttribute.fax_number,
                  lastName: faxNumberAttribute.last_name,
                }
              },
            ),
        }
      })

      setPractices(appConfiguredApps)
      setAppLoading(false)
    } catch (error) {
      console.error("Failed to fetch applications", error)
      setAppLoading(false)
    } finally {
      setflgPracticeCallCompleted(true)
    }
  }

  const handleFaxClick = async (groupName, faxName) => {
    try {
      setFaxLoading(true)
      setFaxLoading(false)
    } catch (error) {
      setFaxLoading(false)
      console.error("Error in handleFaxClick:", error)
    }
  }

  const handleLogout = async () => {
    try {
      await post(`/oauth/logout`)
      Cookies.remove("user")
      //Cookies.remove("token")
      window.location.href = "/"
    } catch (err) {
      console.error("Error in handleLogout:", err)
    }
  }

  useEffect(() => {
    setSelectedPractice(null)
    setSelectedFax(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!["inbound", "outbound"].includes(tab)) {
      setSelectedFax(null)
      setSelectedPractice(null)
    }
  }, [tab])

  useEffect(() => {
    setSelectedFax(null)
    if (selectedPractice && tab === "setting") {
      setTab("inbound")
    } else if (practices.length === 0 && flgPracticeCallCompleted) {
      setTab("setting")
    } else if (selectedPractice?.type === "subfolder" && tab === "outbound") {
      setTab("inbound")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractice, flgPracticeCallCompleted])

  useEffect(() => {
    setFlgReloadFaxes(false)
  }, [selectedFax])

  const loadPractices = async () => {
    setAppLoading(true)
    fetchPractices()
  }

  useEffect(() => {
    loadPractices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="container">
      <div className="header-container">
        <div className="logo-container">
          <img src={FormsLogo} height={36} width={200} alt="Consensus Logo" />
          <div
            className="logo-label"
            style={{ color: "#FF0000", padding: "0px 5px" }}
          >
            |
          </div>
          <div className="logo-label">Tidelands</div>
        </div>
        <div className="user-container" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <User size={16} />{authUser?.emailAddress} |{" "}
          <span className="logout-link" onClick={() => handleLogout()}>
            Logout
          </span>
        </div>
      </div>

      {practices?.length === 0 &&
      !appLoading &&
      authUser?.userRole !== "SUPER_ADMIN" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center", // center horizontally
            textAlign: "center", // center text inside each item
            marginTop: "200px",
          }}
        >
          <h2>Welcome to Tidelands!</h2>
          <span>
            Your account is currently not associated with any practice or
            subfolders.
          </span>
          <span>
            Please contact your administrator or support team to complete your
            setup.
          </span>
        </div>
      ) : (
        <div className="container-fax" ref={containerRef}>
          <div className={`document-list`}>
            <PracticeList
              practices={practices}
              appLoading={appLoading}
              selectedPractice={selectedPractice}
              authUser={authUser}
              setSelectedPractice={setSelectedPractice}
              tab={tab}
              fetchPractices={loadPractices}
            />
          </div>

          <FaxList
            onFaxClick={handleFaxClick}
            selectedFax={selectedFax}
            selectedPractice={selectedPractice}
            setSelectedFax={(fax) => setSelectedFax(fax)}
            flgReloadFaxes={flgReloadFaxes}
            tab={tab}
            setTab={setTab}
            tabs={tabs}
            authUser={authUser}
            reloadPractices={() => fetchPractices()}
            practicesLength={practices.length}
            practices={practices}
            setSelectedPractice={setSelectedPractice}
            appLoading={appLoading}
            faxListWidth={selectedFax && ["inbound", "outbound"].includes(tab) ? 85 - faxViewWidth : 85}
            faxViewWidth={faxViewWidth}
          />

          {selectedFax && ["inbound", "outbound"].includes(tab) && (
            <>
              <div className="panel-resizer" onMouseDown={onMouseDown} />
              <FaxView
                selectedFax={selectedFax}
                faxLoading={faxLoading}
                fetchFaxes={() => setFlgReloadFaxes(true)}
                tab={tab}
                selectedPractice={selectedPractice}
                authUser={authUser}
                onClose={() => setSelectedFax(null)}
                style={{ flex: `0 0 ${faxViewWidth}%`, maxWidth: `${faxViewWidth}%` }}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Dashboard
